import { PostHogProvider, usePostHog } from '@posthog/react';
import type { PostHog, PostHogConfig } from 'posthog-js';
import * as React from 'react';

import { useSession } from '@/components/session-provider';

const apiKey = process.env.EXPO_PUBLIC_POSTHOG_KEY;
const apiHost = process.env.EXPO_PUBLIC_POSTHOG_HOST;

export type AnalyticsClient = Pick<PostHog, 'capture'> & {
  featureFlagsAvailable: boolean;
  isFeatureEnabled: (key: string) => boolean | undefined;
  onFeatureFlags: (callback: () => void) => () => void;
};

const disabledAnalytics: AnalyticsClient = {
  capture: () => undefined,
  featureFlagsAvailable: false,
  isFeatureEnabled: () => false,
  onFeatureFlags: () => () => undefined,
};

const AnalyticsContext = React.createContext<AnalyticsClient>(disabledAnalytics);

const options: Partial<PostHogConfig> = {
  api_host: apiHost,
  ui_host: 'https://us.posthog.com',
  defaults: '2026-05-30',
  autocapture: true,
  capture_pageview: 'history_change',
  capture_pageleave: true,
  capture_performance: true,
  capture_heatmaps: true,
  capture_dead_clicks: true,
  capture_exceptions: {
    capture_unhandled_errors: true,
    capture_unhandled_rejections: true,
    capture_console_errors: true,
  },
  disable_session_recording: false,
  session_recording: {
    maskAllInputs: true,
    recordHeaders: false,
    recordBody: false,
  },
  enable_recording_console_log: true,
  logs: {
    captureConsoleLogs: true,
    serviceName: '6am-web',
    environment: process.env.NODE_ENV ?? 'development',
  },
  mask_personal_data_properties: true,
  custom_personal_data_properties: ['email', 'phone', 'password', 'token', 'authorization', 'code'],
  disable_capture_url_hashes: true,
};

function AnalyticsRuntime({ children }: Readonly<{ children: React.ReactNode }>) {
  const posthog = usePostHog();
  const { profile, session } = useSession();
  const identifiedUserId = React.useRef<string | null>(null);
  const initializationLogged = React.useRef(false);

  React.useEffect(() => {
    if (initializationLogged.current) {
      return;
    }

    posthog.captureLog({
      body: 'Analytics runtime initialized',
      level: 'info',
      attributes: { platform: 'web' },
    });
    initializationLogged.current = true;
  }, [posthog]);

  React.useEffect(() => {
    const userId = session?.user.id ?? null;

    if (userId) {
      const email = profile?.email ?? session?.user.email;
      const name = profile?.full_name;
      const role = profile?.role;

      posthog.identify(userId, {
        ...(email ? { email } : {}),
        ...(name ? { name } : {}),
        ...(role ? { role } : {}),
      });
      identifiedUserId.current = userId;
      return;
    }

    if (identifiedUserId.current) {
      posthog.reset();
      identifiedUserId.current = null;
    }
  }, [
    posthog,
    profile?.email,
    profile?.full_name,
    profile?.role,
    session?.user.email,
    session?.user.id,
  ]);

  const analytics = React.useMemo<AnalyticsClient>(
    () => ({
      capture: posthog.capture.bind(posthog),
      featureFlagsAvailable: true,
      isFeatureEnabled: (key) => posthog.isFeatureEnabled(key),
      onFeatureFlags: (callback) => posthog.onFeatureFlags(callback),
    }),
    [posthog]
  );

  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics() {
  return React.useContext(AnalyticsContext);
}

export function AnalyticsProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  if (!apiKey) {
    return (
      <AnalyticsContext.Provider value={disabledAnalytics}>{children}</AnalyticsContext.Provider>
    );
  }

  return (
    <PostHogProvider apiKey={apiKey} options={options}>
      <AnalyticsRuntime>{children}</AnalyticsRuntime>
    </PostHogProvider>
  );
}
